import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import Tooltip from '../components/Tooltip'

export const Route = createFileRoute('/_layout/positions')({
  component: PositionsComponent,
})

interface Position {
  id: string
  description: string
  status: string
  platforms: string[]
  invested: number
  pnl: number
  return_pct: number
  opened_at: number
}

interface PositionStats {
  totalPositions: number
  openPositions: number
  totalPnL: number
  totalInvested: number
  avgReturn: number
}

function PositionsComponent() {
  const [positions, setPositions] = useState<Position[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expandedPositions, setExpandedPositions] = useState(new Set<string>())
  
  const [positionStats, setPositionStats] = useState<PositionStats>({
    totalPositions: 0,
    openPositions: 0,
    totalPnL: 0,
    totalInvested: 0,
    avgReturn: 0
  })

  useEffect(() => {
    loadPositions()
  }, [])

  const loadPositions = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch('https://skybit-api.botnet-599.workers.dev/positions')
      if (!response.ok) {
        // Use mock data for now since the endpoint doesn't exist
        console.warn('Positions endpoint not found, using mock data')
        const mockData = {
          positions: [
            {
              id: '1',
              description: 'Trump wins 2024 / Biden wins 2024',
              status: 'open',
              platforms: ['Polymarket', 'Kalshi'],
              invested: 5000,
              pnl: 234.50,
              return_pct: 4.69,
              opened_at: Date.now() / 1000 - 86400 * 2
            },
            {
              id: '2',
              description: 'BTC > $100k by EOY / BTC < $100k by EOY',
              status: 'closed',
              platforms: ['Predictit', 'Polymarket'],
              invested: 2500,
              pnl: -125.00,
              return_pct: -5.00,
              opened_at: Date.now() / 1000 - 86400 * 7
            }
          ]
        }
        setPositions(mockData.positions)
        const stats = calculateStats(mockData.positions)
        setPositionStats(stats)
        return
      }
      
      const data = await response.json()
      setPositions(data.positions || [])
      
      // Calculate stats
      const stats = calculateStats(data.positions || [])
      setPositionStats(stats)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load positions')
    } finally {
      setLoading(false)
    }
  }

  const calculateStats = (positions: Position[]): PositionStats => {
    const open = positions.filter(p => p.status === 'open')
    const totalPnL = positions.reduce((sum, p) => sum + (p.pnl || 0), 0)
    const totalInvested = positions.reduce((sum, p) => sum + (p.invested || 0), 0)
    
    return {
      totalPositions: positions.length,
      openPositions: open.length,
      totalPnL,
      totalInvested,
      avgReturn: totalInvested > 0 ? (totalPnL / totalInvested) * 100 : 0
    }
  }

  const togglePosition = (id: string) => {
    const newExpanded = new Set(expandedPositions)
    if (newExpanded.has(id)) {
      newExpanded.delete(id)
    } else {
      newExpanded.add(id)
    }
    setExpandedPositions(newExpanded)
  }

  const formatCurrency = (amount: number) => {
    if (!amount) return '$0.00'
    const absAmount = Math.abs(amount)
    if (absAmount >= 1000000) return `$${(amount / 1000000).toFixed(2)}M`
    if (absAmount >= 1000) return `$${(amount / 1000).toFixed(1)}K`
    return `$${amount.toFixed(2)}`
  }

  const formatDate = (timestamp: number) => {
    if (!timestamp) return 'N/A'
    const date = new Date(timestamp * 1000)
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'var(--sacred-blue)'
      case 'closed': return 'var(--sacred-text-dim)'
      case 'winning': return 'var(--sacred-green)'
      case 'losing': return 'var(--sacred-red)'
      default: return 'var(--sacred-text)'
    }
  }

  if (loading && positions.length === 0) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        <p>Loading positions...</p>
      </div>
    )
  }

  if (error) {
    return <div className="error">Error: {error}</div>
  }

  return (
    <>
      {/* Position Stats */}
      <div style={{ 
        padding: '0.75rem 1rem', 
        borderBottom: '1px solid var(--sacred-border)', 
        background: 'var(--sacred-surface)',
        fontSize: '0.65rem',
        display: 'flex',
        gap: '2rem',
        alignItems: 'center'
      }}>
        <div>
          <span style={{ color: 'var(--sacred-text-dim)' }}>POSITIONS: </span>
          <strong>{positionStats.openPositions}</strong> OPEN / {positionStats.totalPositions} TOTAL
        </div>
        <div>
          <span style={{ color: 'var(--sacred-text-dim)' }}>P&L: </span>
          <strong style={{ 
            color: positionStats.totalPnL >= 0 ? 'var(--sacred-green)' : 'var(--sacred-red)' 
          }}>
            {positionStats.totalPnL >= 0 ? '+' : ''}{formatCurrency(positionStats.totalPnL)}
          </strong>
        </div>
        <div>
          <span style={{ color: 'var(--sacred-text-dim)' }}>RETURN: </span>
          <strong style={{ 
            color: positionStats.avgReturn >= 0 ? 'var(--sacred-green)' : 'var(--sacred-red)' 
          }}>
            {positionStats.avgReturn >= 0 ? '+' : ''}{positionStats.avgReturn.toFixed(1)}%
          </strong>
        </div>
      </div>

      {/* Positions Table */}
      <table className="data-table">
        <thead>
          <tr>
            <th>
              <Tooltip content="The arbitrage strategy and markets">
                Position
              </Tooltip>
            </th>
            <th>
              <Tooltip content="Current status of the position">
                Status
              </Tooltip>
            </th>
            <th>
              <Tooltip content="Platforms involved in the arbitrage">
                Platforms
              </Tooltip>
            </th>
            <th>
              <Tooltip content="Total amount invested">
                Invested
              </Tooltip>
            </th>
            <th>
              <Tooltip content="Current profit/loss">
                P&L
              </Tooltip>
            </th>
            <th>
              <Tooltip content="Return on investment percentage">
                Return %
              </Tooltip>
            </th>
            <th>
              <Tooltip content="When the position was opened">
                Opened
              </Tooltip>
            </th>
            <th>
              <Tooltip content="Available actions">
                Actions
              </Tooltip>
            </th>
          </tr>
        </thead>
        <tbody>
          {positions.length === 0 ? (
            <tr>
              <td colSpan={8} style={{ textAlign: 'center', padding: '2rem' }}>
                <div className="empty-state">
                  <h3>No positions yet</h3>
                  <p>Executed arbitrage positions will appear here</p>
                </div>
              </td>
            </tr>
          ) : (
            positions.map(position => (
              <tr 
                key={position.id}
                className={`position-row ${expandedPositions.has(position.id) ? 'expanded' : ''}`}
                onClick={() => togglePosition(position.id)}
                style={{ cursor: 'pointer' }}
              >
                <td>
                  <span className="expand-icon">
                    {expandedPositions.has(position.id) ? '▼' : '▶'}
                  </span>
                  {position.description || 'Arbitrage Position'}
                </td>
                <td>
                  <span style={{ 
                    color: getStatusColor(position.status),
                    textTransform: 'uppercase',
                    fontSize: '0.6rem',
                    fontWeight: 'bold'
                  }}>
                    {position.status}
                  </span>
                </td>
                <td>{position.platforms?.join(' ↔ ') || 'N/A'}</td>
                <td>{formatCurrency(position.invested)}</td>
                <td style={{ 
                  color: position.pnl >= 0 ? 'var(--sacred-green)' : 'var(--sacred-red)',
                  fontWeight: 'bold'
                }}>
                  {position.pnl >= 0 ? '+' : ''}{formatCurrency(position.pnl)}
                </td>
                <td style={{ 
                  color: position.return_pct >= 0 ? 'var(--sacred-green)' : 'var(--sacred-red)' 
                }}>
                  {position.return_pct >= 0 ? '+' : ''}{position.return_pct?.toFixed(1)}%
                </td>
                <td>{formatDate(position.opened_at)}</td>
                <td>
                  {position.status === 'open' && (
                    <button 
                      className="action-btn small"
                      onClick={(e) => {
                        e.stopPropagation()
                        // Close position logic
                      }}
                    >
                      CLOSE
                    </button>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {positions.length > 0 && (
        <div style={{ 
          padding: '1rem',
          fontSize: '0.6rem',
          color: 'var(--sacred-text-dim)',
          textAlign: 'center'
        }}>
          Showing {positions.length} positions
        </div>
      )}
    </>
  )
}